import * as React from 'react';

export type ColorMode = 'light' | 'dark';

const STORAGE_KEY = 'edge-color-mode';
const DARK_CLASS = 'pf-v6-theme-dark';

const getStoredMode = (): ColorMode | null => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === 'light' || value === 'dark') {
      return value;
    }
  } catch {
    // ignore storage access errors
  }
  return null;
};

const getPreferredMode = (): ColorMode => {
  const stored = getStoredMode();
  if (stored) {
    return stored;
  }
  return 'light';
};

export const applyColorMode = (mode: ColorMode): void => {
  const root = document.documentElement;
  root.classList.toggle(DARK_CLASS, mode === 'dark');
  root.style.colorScheme = mode;
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // ignore storage access errors
  }
};

interface ThemeContextValue {
  colorMode: ColorMode;
  isDark: boolean;
  setColorMode: (mode: ColorMode) => void;
  toggleColorMode: () => void;
}

const ThemeContext = React.createContext<ThemeContextValue | undefined>(undefined);

export const ThemeProvider: React.FunctionComponent<{ children: React.ReactNode }> = ({ children }) => {
  const [colorMode, setColorModeState] = React.useState<ColorMode>(() => getPreferredMode());

  React.useEffect(() => {
    applyColorMode(colorMode);
  }, [colorMode]);

  const setColorMode = React.useCallback((mode: ColorMode) => {
    setColorModeState(mode);
  }, []);

  const toggleColorMode = React.useCallback(() => {
    setColorModeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const value = React.useMemo(
    () => ({
      colorMode,
      isDark: colorMode === 'dark',
      setColorMode,
      toggleColorMode,
    }),
    [colorMode, setColorMode, toggleColorMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
